import Vue from 'vue';
import Component from 'vue-class-component';
import {CustomerDataDTO} from '../../../DTOs';
import {Prop, Watch} from 'vue-property-decorator';
import {Country, State, Title} from '../../../Models';
import {mapGetters} from 'vuex';
import {AddressTypeEnum, SalutationEnum, TitleEnum} from '../../../Enums';
import {CountryService, StateService} from '../../../Services';

@Component({
  computed: {
    ...mapGetters({
      userLanguage: 'getUserLanguage',
      countries: 'getCountries',
      titles: 'getTitles',
      statesMapping: 'getStates',
    }),
  },
})
export default class CustomerForm extends Vue {
  @Prop({type: String, required: true}) context: string | undefined;
  @Prop({type: String, required: true}) inventoryUrl: string | undefined;
  @Prop({type: Boolean, required: false, default: false}) showErrors: boolean | undefined;
  @Prop({type: Boolean, required: false, default: false}) mailConfirm: boolean | undefined;
  @Prop({type: CustomerDataDTO, required: true}) customerDataDTO: CustomerDataDTO | undefined;
  @Prop({
    required: false,
    default() {
      return ['firstName', 'email'];
    },
  }) requiredFields: Array<string> | undefined;
  @Prop({
    required: false,
    default() {
      return [];
    },
  }) disabledFields: Array<string> | undefined;

  // from computed mapGetters
  private userLanguage!: string;
  private countries!: Array<Country>;
  private titles!: Array<Title>;
  private statesMapping!: { [key: string]: Array<State> };

  private dto: CustomerDataDTO = new CustomerDataDTO();
  private stateService!: StateService;
  private countryService!: CountryService;

  private isZipValid: boolean = true;

  get hasCountry(): boolean {
    return Boolean(this.dto?.country);
  }

  get titleChoices() {
    return [
      {value: TitleEnum.NONE.value, text: TitleEnum.NONE.title},
      ...this.titles.map(t => ({value: t.id, text: t.title})),
    ];
  }

  get salutationChoices() {
    return [
      {value: SalutationEnum.FEMALE.value, text: SalutationEnum.FEMALE.title[this.userLanguage]},
      {value: SalutationEnum.MALE.value, text: SalutationEnum.MALE.title[this.userLanguage]},
      {value: SalutationEnum.NONE.value, text: SalutationEnum.NONE.title[this.userLanguage]},
    ];
  }

  get selectedSalutation() {
    const salutation = this.salutationChoices.find(sc => sc.value === this.dto.salutation);
    return salutation ? salutation.text : '';
  }

  get selectedTitle() {
    return this.titleChoices.find(tc => tc.value === this.dto.title);
  }

  get stateChoices() {
    if (this.statesMapping && this.dto && this.dto.country && this.statesMapping[this.dto.country]) {
      return this.statesMapping[this.dto.country];
    }
    return [];
  }

  get isBilling() {
    return this.context === 'invoice';
  }

  get isGuestLogin() {
    return this.context === 'guestlogin';
  }

  get isPersonalization() {
    return this.context === 'personalization';
  }

  get addressTypeChoices() {
    return [
      {value: AddressTypeEnum.NONE.value, text: AddressTypeEnum.NONE.title[this.userLanguage]},
      {value: AddressTypeEnum.PRIVATE.value, text: AddressTypeEnum.PRIVATE.title[this.userLanguage]},
      {value: AddressTypeEnum.COMMERCIAL_BUSINESS.value, text: AddressTypeEnum.COMMERCIAL_BUSINESS.title[this.userLanguage]},
    ];
  }

  get currentAddressType() {
    switch (this.customerDataDTO?.addressType) {
      case AddressTypeEnum.NONE.value:
        return AddressTypeEnum.NONE.title[this.userLanguage];
      case AddressTypeEnum.PRIVATE.value:
        return AddressTypeEnum.PRIVATE.title[this.userLanguage];
      case AddressTypeEnum.COMMERCIAL_BUSINESS.value:
        return AddressTypeEnum.COMMERCIAL_BUSINESS.title[this.userLanguage];
      default:
        return AddressTypeEnum.NONE.title[this.userLanguage];
    }
  }

  created() {
    this.stateService = new StateService(this.inventoryUrl);
    this.countryService = new CountryService(this.inventoryUrl);
  }

  // We need this part to be present for personalization when the page is first loaded. The currentIndex check alone is not sufficient for personalization page.
  mounted() {
    if (this.customerDataDTO) {
      this.dto = (new CustomerDataDTO()).fromData(this.customerDataDTO);
    }
    const hasSentDataLayer = sessionStorage.getItem('sentDataLayerPayment') === '1';
    if (!hasSentDataLayer) {
      if (this.context === 'personalization') {
        // @ts-ignore
        window.dataLayer.push({
          event: 'checkout_personalisation',
        });
        sessionStorage.setItem('sentDataLayerPayment', '1');
      }
    }
  }
  @Watch('dto', {deep: true})
  onCustomerDataDTOChanged(val: CustomerDataDTO, oldVal: CustomerDataDTO) {
    this.$emit('update', {dto: val, missingRequiredFields: this.missingRequiredFields});
  }

  @Watch('dto.zip', {deep: true})
  async onCustomerDataDTOZipChanged(val: string, oldVal: string) {
    if (val.length === 5 && this.hasSelectedGermany && this.context === 'guestlogin') {
      try {
        const state = await this.stateService.findByZip(Number.parseInt(val, 10));
        if (state && state.city) {
          Vue.set(this.dto, 'city', state.city.title);
        }
        if (state && state.id && this.stateChoices && this.stateChoices.length > 0) {
          Vue.set(this.dto, 'state', state.id);
        }
      } catch (e) {
        console.error(e);
      }
    }
  }

  async fetchStatesOf(countryId: number) {
    if (!this.statesMapping[countryId] || this.statesMapping[countryId].length === 0) {
      const states = await this.stateService.listCached<State>(countryId.toString(10));
      Vue.set(this.statesMapping, countryId, states);
    }
  }

  @Watch('dto.country')
  async onCountryChange(newVal: number, oldVal: number) {
    if (this.dto.country) {
      await this.fetchStatesOf(newVal);
      const hasSelectedStateForCountry = this.stateChoices.find(s => s.id === this.dto.state) !== undefined;
      if (!hasSelectedStateForCountry && this.stateChoices.length > 0) {
        this.dto.state = this.stateChoices[0].id as number;
      }
    }
  }

  /**
   * Make sure external changes to the dto are reflected in the internal instance.
   * @param val
   * @param oldVal
   */
  @Watch('customerDataDTO', {deep: true})
  onExternalDTOChange(val: CustomerDataDTO, oldVal: CustomerDataDTO) {
    Object.keys(val).forEach(k => {
      if (this.dto) {
        Vue.set(this.dto, k, val[k]);
      }
    });
  }

  get missingRequiredFields(): Array<string> {
    if (this.requiredFields && this.requiredFields.length > 0) {
      return this.requiredFields.filter(f => {
        switch (f) {
          case 'country':
          case 'title':
            return !this.isSelectValSet(this.dto[f]);
          case 'state':
            return this.stateChoices.length !== 0 && !this.isSelectValSet(this.dto.country);
          case 'addressType':
            return this.dto?.addressType === AddressTypeEnum.NONE.value;
          case 'company':
            return this.showCompany() && !this.isValSet(this.dto[f]);
          default:
            return !this.isValSet(this.dto[f]);
        }
      });
    }
    return [];
  }

  get isMailMatching(): boolean {
    return this.showErrors === true && (this.dto.email.toLowerCase() !== this.dto.emailConfirm.toLowerCase() || this.dto.emailConfirm === '');
  }

  showCompany() {
    // only required if address-type is non-private
    return this.dto?.addressType === AddressTypeEnum.COMMERCIAL_BUSINESS.value;
  }

  countryTitle(country: Country) {
    if (country) {
      if (this.userLanguage !== 'en' && country.translations && country.translations[this.userLanguage]) {
        return country.translations[this.userLanguage].title;
      }
      return country.title;
    }
    return '';
  }

  stateTitle(state: State) {
    if (state) {
      if (this.userLanguage !== 'en' && state.translations && state.translations[this.userLanguage]) {
        return state.translations[this.userLanguage].title;
      }
      return state.title;
    }
    return '-';
  }

  requiredValue(fieldName: string) {
    return this.isRequired(fieldName) ? 'required' : '';
  }

  isRequired(fieldName: string) {
    return this.requiredFields && this.requiredFields.indexOf(fieldName) >= 0;
  }

  isDisabled(fieldName: string) {
    return this.disabledFields && this.disabledFields.indexOf(fieldName) >= 0;
  }

  showErrorClass(fieldName: string): boolean {
    return <boolean>this.showErrors && this.missingRequiredFields.includes(fieldName);
  }

  isValSet(val: any): boolean {
    return val !== undefined && val !== null && val !== '';
  }

  isSelectValSet(val: any): boolean {
    return this.isValSet(val) && val !== 0;
  }

  setIsZipValid(value: boolean) {
    this.isZipValid = value;
    this.$emit('isZipValid', value);
  }

  validateZip() {
    let zip = this.dto.zip.replace(' ', '');

    if (this.hasSelectedNetherlands) {
      if (zip.length === 6) {
        const numbers = Number.parseInt(zip.substring(0, 4), 10);
        const letters = zip.substring(4, 6);
        // tests, if constant letters only contains letters
        if (/^[A-Za-z\s]*$/.test(letters) && numbers >= 1000 && numbers <= 9999) {
          this.dto.zip = `${numbers} ${letters}`;
          this.setIsZipValid(true);
        } else {
          this.setIsZipValid(false);
        }
      } else {
        this.setIsZipValid(false);
      }
    } else if (this.hasSelectedSweden || this.hasSelectedCzechRepublic) {
      if (zip.length === 5) {
        const numbers = Number.parseInt(zip, 10);
        if (numbers >= 10000 && numbers <= 99999) {
          this.dto.zip = `${zip.substring(0, 3)} ${zip.substring(3, 5)}`;
          this.setIsZipValid(true);
        } else {
          this.setIsZipValid(false);
        }
      } else {
        this.setIsZipValid(false);
      }
    } else {
      this.setIsZipValid(true);
    }
  }

  get refKey() {
    return `customerForm-${this.context}`;
  }

  get disableStateSelect() {
    return !this.hasCountry || (this.hasCountry && !(this.stateChoices.length > 0));
  }

  get hasSelectedGermany() {
    return this.countries.find(c => c.id === this.dto.country && c.iso31661alpha2 === 'de');
  }

  get hasSelectedNetherlands() {
    return this.countries.find(c => c.id === this.dto.country && c.iso31661alpha2 === 'nl');
  }

  get hasSelectedSweden() {
    return this.countries.find(c => c.id === this.dto.country && c.iso31661alpha2 === 'se');
  }

  get hasSelectedCzechRepublic() {
    return this.countries.find(c => c.id === this.dto.country && c.iso31661alpha2 === 'cz');
  }
}
